'use client';

import { useEffect, useState } from 'react';
import {
  resendVerificationRequest,
  type ResendStatus,
} from '@/features/auth/shared/api/resend-verification-request';
import { readClientCookie } from '@/shared/utils/client-cookies';
import {
  VERIFICATION_LAST_RESEND_COOKIE,
  VERIFICATION_RESEND_STATUS_RESET_TIMEOUT_MS,
} from '@/features/auth/shared/helpers/constants/verification-cookies';

export type UseResendVerificationOptions = {
  email: string | null;
  initialStatus: ResendStatus;
  returnTo?: string;
};

export type UseResendVerificationResult = {
  resendStatus: ResendStatus;
  handleResend: () => Promise<void>;
};

export const useResendVerification = ({
  email,
  initialStatus,
  returnTo,
}: UseResendVerificationOptions): UseResendVerificationResult => {
  const [resendStatus, setResendStatus] = useState<ResendStatus>(initialStatus);

  useEffect(() => {
    if (resendStatus !== 'sent' && resendStatus !== 'cooldown') {
      return;
    }
    let resetDelayMs = VERIFICATION_RESEND_STATUS_RESET_TIMEOUT_MS;
    if (resendStatus === 'cooldown') {
      const lastResendRaw = readClientCookie(VERIFICATION_LAST_RESEND_COOKIE);
      if (lastResendRaw) {
        const elapsedMs = Date.now() - Number(lastResendRaw);
        resetDelayMs = Math.max(VERIFICATION_RESEND_STATUS_RESET_TIMEOUT_MS - elapsedMs, 0);
      }
    }
    const timeoutId = window.setTimeout(() => {
      setResendStatus('idle');
    }, resetDelayMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [resendStatus]);

  const handleResend = async (): Promise<void> => {
    if (!email || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      const status = await resendVerificationRequest(email, returnTo);
      setResendStatus(status);
    } catch {
      setResendStatus('error');
    }
  };

  return { resendStatus, handleResend };
};
